import React, { useEffect } from 'react';
import { imageOptimizer } from '@/lib/ImageOptimizationEngine';

interface CriticalResourceOptimizerProps {
  children: React.ReactNode;
}

/**
 * Critical Resource Optimizer
 * Manages preloading, resource hints, and critical path optimization
 */
const CriticalResourceOptimizer: React.FC<CriticalResourceOptimizerProps> = ({ children }) => {
  useEffect(() => {
    // Mark optimization start
    if (window.performance?.mark) {
      window.performance.mark('critical-resources-optimization-start');
    }

    // 1. Preload critical hero images with multiple formats and sizes
    const heroImageSizes = [
      { width: 480, height: 270, quality: 75, media: '(max-width: 767px)' },
      { width: 800, height: 450, quality: 80, media: '(min-width: 768px) and (max-width: 1023px)' },
      { width: 1200, height: 675, quality: 85, media: '(min-width: 1024px)' }
    ];

    const heroImageSrc = '/lovable-uploads/5e55ee18-c76b-4186-b61e-2f4d7b561b39.png';
    
    heroImageSizes.forEach(({ width, height, quality, media }) => {
      // WebP preload
      const webpLink = document.createElement('link');
      webpLink.rel = 'preload';
      webpLink.href = `${heroImageSrc}?format=webp&width=${width}&height=${height}&quality=${quality}&fit=cover`;
      webpLink.as = 'image';
      webpLink.type = 'image/webp';
      webpLink.media = media;
      (webpLink as any).fetchpriority = 'high';
      document.head.appendChild(webpLink);

      // AVIF preload for modern browsers
      const avifLink = document.createElement('link');
      avifLink.rel = 'preload';
      avifLink.href = `${heroImageSrc}?format=avif&width=${width}&height=${height}&quality=${quality}&fit=cover`;
      avifLink.as = 'image';
      avifLink.type = 'image/avif';
      avifLink.media = media;
      (avifLink as any).fetchpriority = 'high';
      document.head.appendChild(avifLink);
    });

    // 2. Preload critical fonts with optimal loading strategy
    const criticalFonts = [
      { 
        href: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2',
        weight: '400'
      },
      { 
        href: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYAZ9hiA.woff2',
        weight: '600'
      }
    ];

    criticalFonts.forEach(({ href, weight }) => {
      const fontLink = document.createElement('link');
      fontLink.rel = 'preload';
      fontLink.href = href;
      fontLink.as = 'font';
      fontLink.type = 'font/woff2';
      fontLink.crossOrigin = 'anonymous';
      fontLink.setAttribute('data-weight', weight);
      document.head.appendChild(fontLink);
    });

    // 3. DNS prefetch for critical external domains
    const criticalDomains = [
      'https://cdn.webhotelier.net',
      'https://loenaluxurysuites.reserve-online.net',
      'https://analytics.ahrefs.com',
      'https://code.rateparity.com'
    ];

    criticalDomains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = domain;
      document.head.appendChild(link);
    });

    // 4. Preconnect to critical external resources
    const preconnectDomains = [
      { href: 'https://fonts.googleapis.com', crossorigin: true },
      { href: 'https://fonts.gstatic.com', crossorigin: true }
    ];

    preconnectDomains.forEach(({ href, crossorigin }) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      if (crossorigin) link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });

    // 5. Optimize script loading order
    const optimizeScriptLoading = () => {
      // Defer non-critical analytics scripts
      const analyticsScripts = document.querySelectorAll('script[src*="analytics"], script[src*="gtag"]');
      analyticsScripts.forEach(script => {
        if (script instanceof HTMLScriptElement) {
          script.defer = true;
        }
      });

      // Add performance observer for script loading
      if (window.PerformanceObserver) {
        const scriptObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (entry.name.includes('.js') && entry.duration > 100) {
              console.warn(`⚠️ Slow script detected: ${entry.name} (${Math.round(entry.duration)}ms)`);
            }
          });
        });
        scriptObserver.observe({ entryTypes: ['resource'] });
      }
    };

    // 6. Implement resource loading prioritization
    const prioritizeResources = () => {
      // Critical CSS
      const criticalCSS = document.querySelector('link[href*="index.css"]');
      if (criticalCSS instanceof HTMLLinkElement) {
        (criticalCSS as any).fetchpriority = 'high';
      }

      // Main JavaScript bundle
      const mainScript = document.querySelector('script[src*="main"]');
      if (mainScript instanceof HTMLScriptElement) {
        (mainScript as any).fetchpriority = 'high';
      }
    };

    // 7. Implement adaptive loading based on connection
    const adaptiveLoading = () => {
      const connection = (navigator as any)?.connection;
      if (connection) {
        // Reduce quality for slow connections
        if (['slow-2g', '2g'].includes(connection.effectiveType)) {
          document.documentElement.classList.add('slow-connection');
          console.log('🐌 Slow connection detected - optimizing for performance');
        }
        
        // Enable data saver mode
        if (connection.saveData) {
          document.documentElement.classList.add('save-data');
          console.log('💾 Data saver mode enabled');
        }
      }
    };

    // 8. Memory optimization for low-end devices
    const memoryOptimization = () => {
      const deviceMemory = (navigator as any)?.deviceMemory;
      if (deviceMemory && deviceMemory < 4) {
        document.documentElement.classList.add('low-memory');
        console.log(`🧠 Low memory device detected: ${deviceMemory}GB`);
        
        // Reduce concurrent image loading
        if (typeof window !== 'undefined') {
          (window as any).maxConcurrentImageLoads = 2;
        }
      }
    };

    // Execute optimizations
    setTimeout(() => {
      optimizeScriptLoading();
      prioritizeResources();
      adaptiveLoading();
      memoryOptimization();
      
      // Mark completion
      if (window.performance?.mark) {
        window.performance.mark('critical-resources-optimization-complete');
        window.performance.measure(
          'critical-resources-optimization-duration',
          'critical-resources-optimization-start',
          'critical-resources-optimization-complete'
        );
      }
      
      console.log('⚡ Critical resource optimization completed');
    }, 0);

    // 9. Preload critical images using Image Optimization Engine
    imageOptimizer.preloadCriticalImages([heroImageSrc]);

    // 10. Service Worker registration for caching (if available)
    if ('serviceWorker' in navigator && window.location.protocol === 'https:') {
      navigator.serviceWorker.register('/service-worker.js', { scope: '/' })
        .then(() => console.log('🔧 Service Worker registered'))
        .catch(() => console.log('⚠️ Service Worker registration failed'));
    }

  }, []);

  return <>{children}</>;
};

export default CriticalResourceOptimizer;